import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { OptionSelect } from '@/components/OptionSelect';
import { PLATFORM_LABEL } from './buildHelpers';
import { executionModeLabel } from './buildLabels';
import type { ProfileGroup } from './runBuildModel';
import type { RunBuildFormModel } from './useRunBuildForm';

/** Project (only when there are several and none was preset), app and build profile, plus the runner warning. */
export function RunBuildTargetFields({ form }: { form: RunBuildFormModel }) {
  const { projects, project } = form.target;
  const showProject = Boolean(projects.data && projects.data.length > 1 && !form.preset.projectSlug);
  return (
    <>
      {showProject ? <ProjectField form={form} projectId={project?.id ?? ''} /> : null}
      <AppField form={form} />
      <ProfileField form={form} />
      {!form.target.runnerAllowed && form.target.group ? <RunnerWarning /> : null}
    </>
  );
}

function ProjectField({ form, projectId }: { form: RunBuildFormModel; projectId: string }) {
  return (
    <FormField id="run-build-project" label="Project">
      <OptionSelect
        id="run-build-project"
        data-testid="run-build-project"
        value={projectId}
        onValueChange={form.fields.set.setProjectId}
        placeholder="Pick"
        options={(form.target.projects.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
      />
    </FormField>
  );
}

function AppField({ form }: { form: RunBuildFormModel }) {
  const { app, apps } = form.target;
  const { setAppId, setProfileSlug, setPlatforms } = form.fields.set;
  const error = form.errorFor('app');
  const options = apps.data ?? [];
  return (
    <FormField id="run-build-app" label="App" required error={error} trailing={app ? <span className="font-mono text-xs text-subtle">{app.workingDirectory}</span> : undefined}>
      <OptionSelect
        data-testid="run-build-app"
        value={app?.id ?? ''}
        onValueChange={(appId) => {
          setAppId(appId);
          setProfileSlug('');
          setPlatforms([]);
        }}
        placeholder={options.length === 0 ? 'No apps in this project' : 'Pick'}
        options={options.map((a) => ({ value: a.id, label: `${a.slug} — ${a.name}` }))}
        className="font-mono sm:text-[13px]"
        contentClassName="font-mono"
        {...fieldA11y('run-build-app', error)}
      />
    </FormField>
  );
}

/** How the profile group's builds are made ("Built by Hangar", "From your CI"), as tags. */
function ExecutionModes({ group }: { group: ProfileGroup }) {
  return (
    <span className="flex gap-1">
      {[...new Set(group.list.flatMap((p) => p.executionModes))].map((m) => (
        <Tag key={m} className="font-sans text-muted-foreground">
          {executionModeLabel(m)}
        </Tag>
      ))}
    </span>
  );
}

function ProfileField({ form }: { form: RunBuildFormModel }) {
  const { group, groups } = form.target;
  const { setProfileSlug, setPlatforms } = form.fields.set;
  const error = form.errorFor('profile');
  return (
    <FormField id="run-build-profile" label="Build profile" required error={error} trailing={group ? <ExecutionModes group={group} /> : undefined}>
      <OptionSelect
        data-testid="run-build-profile"
        value={group?.slug ?? ''}
        onValueChange={(slug) => {
          setProfileSlug(slug);
          setPlatforms([]);
        }}
        placeholder={groups.length === 0 ? 'No profiles' : 'Pick'}
        options={groups.map((g) => ({ value: g.slug, label: `${g.name} (${g.slug}) · ${g.list.map((p) => PLATFORM_LABEL[p.platform]).join(' + ')}` }))}
        {...fieldA11y('run-build-profile', error)}
      />
    </FormField>
  );
}

function RunnerWarning() {
  return (
    <p className="flex items-start gap-2 rounded-md border border-awaiting/30 bg-awaiting/10 px-2.5 py-1.5 text-[13px] text-awaiting">
      <Icon name="warning" size={16} className="mt-px" />
      Hangar cannot build this profile itself: the build follows the profile's own setting.
    </p>
  );
}
